import { useRef, useState, useCallback, type FC } from 'react';
import { graphql, useFragment } from 'react-relay';
import { useDispatch } from 'react-redux';
import { FormattedMessage } from 'dibs-react-intl';
import { Button } from 'dibs-elements/exports/Button.tsx';
import { Link } from 'dibs-elements/exports/Link.tsx';
import ChevronRight from 'dibs-icons/exports/ChevronRight.ts';
import ChevronLeft from 'dibs-icons/exports/ChevronLeft.ts';
import Filter from 'dibs-icons/exports/Filter.ts';
import { getValidSharedAppliedFilters } from '../SbSharedAppliedFilters/sbSharedAppliedFiltersHelpers.ts';
import { trackEvent, eventNameConstants, interactionTypeConstants } from 'dibs-tracking';
import { SbRespQuickFilterDropdown } from '../SbRespQuickFilterDropdown/SbRespQuickFilterDropdown.tsx';

import { useDebouncedCallback } from 'dibs-react-hooks/exports/useDebouncedCallback.js';
import { useResizeObserver } from 'dibs-react-hooks/exports/useResizeObserver.ts';
import { useClientState } from 'dibs-react-hooks/exports/useClientState.ts';
import { toggleFilterDisplay } from '../../actions/filterActions.ts';
import { useSbSelector } from '../../reducers/useSbSelector.ts';
import { hasKey } from 'dibs-ts-utils/exports/hasKey.ts';
import {
    QUICK_FILTERS_BY_VERTICAL,
    QUICK_FILTERS_EMPTY_ARRAY,
} from '../SbSharedRefineMenu/sbSharedQuickFiltersConstants.ts';

import classnames from 'classnames';
import dibsCss from 'dibs-css';
import styles from './SbRespQuickFilters.module.css';

import { type SbRespQuickFilters_viewer$key } from './__generated__/SbRespQuickFilters_viewer.graphql.ts';
import { type SbRespQuickFilters_itemSearch$key } from './__generated__/SbRespQuickFilters_itemSearch.graphql.ts';
import { type SbRespQuickFilters_user$key } from './__generated__/SbRespQuickFilters_user.graphql.ts';

const viewerFragment = graphql`
    fragment SbRespQuickFilters_viewer on Viewer {
        ...SbRespQuickFilterDropdown_viewer
    }
`;

const itemSearchFragment = graphql`
    fragment SbRespQuickFilters_itemSearch on ItemSearchQueryConnection {
        topCategoryL1
        ...SbRespQuickFilterDropdown_itemSearch
        appliedFilters {
            canBeDismissed
        }
    }
`;
const userFragment = graphql`
    fragment SbRespQuickFilters_user on User {
        ...SbRespQuickFilterDropdown_user
    }
`;

type Props = {
    viewer: SbRespQuickFilters_viewer$key;
    itemSearch: SbRespQuickFilters_itemSearch$key;
    user: SbRespQuickFilters_user$key | null | undefined;
};

export const SbRespQuickFilters: FC<Props> = ({
    viewer: viewerRef,
    itemSearch: itemSearchRef,
    user: userRef,
}) => {
    const dispatch = useDispatch();
    const viewer = useFragment(viewerFragment, viewerRef);
    const itemSearch = useFragment(itemSearchFragment, itemSearchRef);
    const user = useFragment(userFragment, userRef);
    const vertical = itemSearch.topCategoryL1 || '';
    const isClient = useClientState();
    const refetchInFlight = useSbSelector(state => state.filters.refetchInFlight);
    const isLoading = !isClient || refetchInFlight;
    const [activeFilter, setActiveFilter] = useState<string>('');

    const filterNames = hasKey(QUICK_FILTERS_BY_VERTICAL, vertical)
        ? QUICK_FILTERS_BY_VERTICAL[vertical]
        : QUICK_FILTERS_EMPTY_ARRAY;

    const scrollContainerRef = useRef<HTMLDivElement>(null);
    const [canScrollLeft, setCanScrollLeft] = useState(false);
    const [canScrollRight, setCanScrollRight] = useState(false);

    const checkScrollability = useCallback(() => {
        const el = scrollContainerRef.current;
        if (el) {
            const { scrollLeft, scrollWidth, clientWidth } = el;
            const newCanScrollLeft = scrollLeft > 1;
            if (newCanScrollLeft !== canScrollLeft) {
                setCanScrollLeft(newCanScrollLeft);
            }
            // Math.floor is needed below, sometimes scrollLeft comes back with .5 precision resulting in a 0.5 difference when we expect 0
            const newCanScrollRight = Math.floor(scrollWidth - scrollLeft - clientWidth) > 1;
            if (newCanScrollRight !== canScrollRight) {
                setCanScrollRight(newCanScrollRight);
            }
        }
    }, [canScrollLeft, canScrollRight]);

    const [onResize] = useDebouncedCallback(checkScrollability);
    useResizeObserver(scrollContainerRef, onResize);

    const handleScrollArrowClick = useCallback((direction: 'left' | 'right') => {
        const el = scrollContainerRef.current;
        if (el) {
            const scrollAmount = Math.max(el.clientWidth * 0.25, 200);
            const targetScrollLeft =
                direction === 'left' ? el.scrollLeft - scrollAmount : el.scrollLeft + scrollAmount;
            el.scrollTo({ left: targetScrollLeft, behavior: 'smooth' });
        }
    }, []);

    const appliedFilters = (itemSearch.appliedFilters || []).map(appliedFilter => ({
        canBeDismissed: !!appliedFilter?.canBeDismissed,
    }));
    const validAppliedFilters = getValidSharedAppliedFilters(appliedFilters);
    const appliedFiltersCount = validAppliedFilters.length;

    return (
        <>
            <div
                className={classnames(
                    dibsCss.relative,
                    dibsCss.overflowHidden,
                    dibsCss.flexGrow,
                    dibsCss.zMiddle
                )}
            >
                <Link
                    className={classnames(
                        styles.arrow,
                        styles.arrowLeft,
                        dibsCss.absolute,
                        dibsCss.left0,
                        dibsCss.top0,
                        dibsCss.zHigh,
                        {
                            [styles.isHidden]: !canScrollLeft,
                        }
                    )}
                    onClick={() => handleScrollArrowClick('left')}
                >
                    <ChevronLeft variation="20" />
                </Link>
                <div
                    ref={scrollContainerRef}
                    onScroll={checkScrollability}
                    className={classnames(
                        styles.hideScrollbar,
                        dibsCss.overflowScroll,
                        dibsCss.flex,
                        dibsCss.flexGrow,
                        dibsCss.itemsCenter,
                        dibsCss.gapXxsmall
                    )}
                >
                    {filterNames.map(filterName => (
                        <SbRespQuickFilterDropdown
                            key={filterName}
                            viewer={viewer}
                            itemSearch={itemSearch}
                            user={user}
                            isActive={activeFilter === filterName}
                            isLoading={isLoading}
                            filterName={filterName}
                            setActiveFilter={setActiveFilter}
                        />
                    ))}
                </div>
                <Link
                    className={classnames(
                        styles.arrow,
                        styles.arrowRight,
                        dibsCss.absolute,
                        dibsCss.right0,
                        dibsCss.top0,
                        dibsCss.zHigh,
                        {
                            [styles.isHidden]: !canScrollRight,
                        }
                    )}
                    onClick={() => handleScrollArrowClick('right')}
                >
                    <ChevronRight variation="20" />
                </Link>
            </div>
            <Button
                style="circle"
                size="small"
                type="primaryAlt"
                className={classnames(styles.button, dibsCss.itemsCenter, dibsCss.gapXxsmall)}
                onClick={e => {
                    e.currentTarget.blur();
                    trackEvent(
                        {
                            category: 'results page facet selected',
                            action: 'refine link clicked',
                            eventName: eventNameConstants.EVENT_RESULTS_PAGE_FACET_SELECTED,
                            interaction_type:
                                interactionTypeConstants.INTERACTION_TYPE_FILTER_RESULTS_LINK_CLICKED,
                            step_interaction_name: 'show filters',
                        },
                        e
                    );
                    dispatch(toggleFilterDisplay());
                }}
                disabled={isLoading}
            >
                <div className={classnames(dibsCss.flex, dibsCss.itemsCenter, dibsCss.gapXxsmall)}>
                    <Filter variation="20" />
                    <FormattedMessage
                        id="sb.SbMobileRefineMenuHeader.AllFilters"
                        defaultMessage="All Filters"
                    />
                    {appliedFiltersCount > 0 && <span>({appliedFiltersCount})</span>}
                </div>
            </Button>
        </>
    );
};
